import React, {useMemo} from "react";
import {Box, IconButton, LinearProgress, makeStyles, Tooltip, Typography} from "@material-ui/core";
import {Cancel, CheckCircle, Error, HourglassEmpty} from "@material-ui/icons";
import {useTranslation} from "react-i18next";
import DataTable from "react-data-table-component";
import {ReanalysisJobStatus, ReanalysisProgress} from "api/deliveryCsvAnalysis";

const useStyles = makeStyles(() => ({
  progressCell: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
  },
  progressBar: {
    flex: 1,
    minWidth: "60px",
  },
  progressText: {
    fontSize: "12px",
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  statusCell: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  statusIcon: {
    fontSize: "16px",
  },
  completedIcon: {
    color: "#4caf50",
  },
  failedIcon: {
    color: "#f44336",
  },
  cancelledIcon: {
    color: "#9e9e9e",
  },
  cancelButton: {
    color: "#f44336",
    padding: "4px",
  },
}));

interface RunningJobsProps {
  jobs: ReanalysisProgress[];
  onCancel: (jobId: string) => void;
  onJobClick?: (job: ReanalysisProgress) => void;
}

export const RunningJobs = (props: RunningJobsProps) => {
  const { jobs, onCancel, onJobClick } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const isActive = (status: string) => {
    return status === ReanalysisJobStatus.PENDING || status === ReanalysisJobStatus.RUNNING;
  };

  const formatFilterValue = (items: string[], allLabel: string, pluralLabel: string) => {
    if (items.length === 0) {
      return allLabel;
    }
    if (items.length === 1) {
      return items[0];
    }
    return `${items.length} ${pluralLabel}`;
  };

  const renderCellWithTooltip = (items: string[], allLabel: string, pluralLabel: string) => {
    const value = formatFilterValue(items, allLabel, pluralLabel);
    if (items.length > 1) {
      return (
        <Tooltip title={items.join(", ")}>
          <span>{value}</span>
        </Tooltip>
      );
    }
    return <span>{value}</span>;
  };

  const renderStatus = (job: ReanalysisProgress) => {
    const status = job.status?.toString() ?? "";
    const active = isActive(status);

    if (active) {
      return (
        <Box className={classes.progressCell}>
          <LinearProgress
            variant="determinate"
            value={(job.processedFiles / job.totalFiles) * 100}
            className={classes.progressBar}
          />
          <Typography className={classes.progressText}>
            {job.processedFiles}/{job.totalFiles}
          </Typography>
          <Tooltip title={t("received_data_analysis_cancel_job")}>
            <IconButton
              className={classes.cancelButton}
              onClick={(e) => {
                e.stopPropagation();
                onCancel(job.jobId);
              }}
              size="small"
            >
              <Cancel fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    return (
      <Box className={classes.statusCell}>
        {status === ReanalysisJobStatus.COMPLETED && (
          <>
            <CheckCircle className={`${classes.statusIcon} ${classes.completedIcon}`} />
            <Typography variant="body2">{t("received_data_analysis_job_completed")}</Typography>
          </>
        )}
        {status === ReanalysisJobStatus.FAILED && (
          <>
            <Error className={`${classes.statusIcon} ${classes.failedIcon}`} />
            <Typography variant="body2">{t("received_data_analysis_job_failed")}</Typography>
          </>
        )}
        {status === ReanalysisJobStatus.CANCELLED && (
          <>
            <HourglassEmpty className={`${classes.statusIcon} ${classes.cancelledIcon}`} />
            <Typography variant="body2">{t("received_data_analysis_job_cancelled")}</Typography>
          </>
        )}
      </Box>
    );
  };

  const columns = useMemo(() => [
    {
      name: t("received_data_analysis_importer"),
      cell: (row: ReanalysisProgress) => renderCellWithTooltip(
        row.importerNames,
        t("received_data_analysis_all"),
        t("received_data_analysis_importers")
      ),
      width: "150px",
    },
    {
      name: t("received_data_analysis_operator"),
      cell: (row: ReanalysisProgress) => renderCellWithTooltip(
        row.operatorNames,
        t("received_data_analysis_all"),
        t("received_data_analysis_operators")
      ),
      width: "150px",
    },
    {
      name: t("received_data_analysis_carrier"),
      cell: (row: ReanalysisProgress) => renderCellWithTooltip(
        row.carrierNames,
        t("received_data_analysis_all"),
        t("received_data_analysis_carriers")
      ),
      width: "150px",
    },
    {
      name: t("received_data_analysis_territory"),
      cell: (row: ReanalysisProgress) => renderCellWithTooltip(
        row.territoryNames,
        t("received_data_analysis_all"),
        t("received_data_analysis_territories")
      ),
      width: "150px",
    },
    {
      name: t("received_data_analysis_filter_upload"),
      cell: (row: ReanalysisProgress) => renderCellWithTooltip(
        (row as any).uploadMonths ?? [],
        t("received_data_analysis_all"),
        t("received_data_analysis_months")
      ),
      width: "120px",
    },
    {
      name: t("received_data_analysis_filter_content"),
      cell: (row: ReanalysisProgress) => renderCellWithTooltip(
        (row as any).contentMonths ?? [],
        t("received_data_analysis_all"),
        t("received_data_analysis_months")
      ),
      width: "120px",
    },
    {
      name: t("received_data_analysis_filter_valid"),
      cell: (row: ReanalysisProgress) => (row as any).query?.successOnly
        ? t("received_data_analysis_valid_only")
        : t("received_data_analysis_all"),
      width: "100px",
    },
    {
      name: t("received_data_analysis_col_status"),
      cell: (row: ReanalysisProgress) => renderStatus(row),
      width: "200px",
    },
  ], [t, onCancel]);

  return (
    <DataTable
      columns={columns}
      data={jobs}
      noHeader
      theme="colisactiv"
      noDataComponent={t("received_data_analysis_no_data")}
      onRowClicked={onJobClick}
      highlightOnHover
      pointerOnHover
    />
  );
};
